import { useState, useEffect } from "react";
import axios from "axios";
import { Card, CardContent } from "@/components/ui/card";
import { Skeleton } from "@/components/ui/skeleton";
import { Package } from "lucide-react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function Lots() {
  const [lots, setLots] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const fetchLots = async () => {
      try {
        const response = await axios.get(`${API}/lots`);
        setLots(response.data);
      } catch (error) {
        console.error("Error fetching lots:", error);
      } finally {
        setLoading(false);
      }
    };
    fetchLots();
  }, []);

  return (
    <div className="py-12 md:py-16" data-testid="lots-page">
      <div className="max-w-7xl mx-auto px-4 md:px-8">
        {/* Header */}
        <div className="text-center mb-12">
          <h1 className="font-heading text-4xl md:text-5xl font-bold text-gray-900">
            Nos Lots
          </h1>
          <p className="mt-4 text-lg text-gray-600 max-w-2xl mx-auto">
            Des assortiments composés avec soin pour répondre à tous vos besoins.
          </p>
        </div>

        {/* Lots Grid */}
        {loading ? (
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
            {[...Array(3)].map((_, i) => (
              <Card key={i} className="overflow-hidden">
                <Skeleton className="aspect-video" />
                <CardContent className="p-6">
                  <Skeleton className="h-7 w-3/4 mb-3" />
                  <Skeleton className="h-4 w-full mb-2" />
                  <Skeleton className="h-20 w-full mb-4" />
                  <Skeleton className="h-6 w-1/3" />
                </CardContent>
              </Card>
            ))}
          </div>
        ) : lots.length === 0 ? (
          <div className="text-center py-12">
            <Package className="h-16 w-16 text-gray-300 mx-auto mb-4" />
            <p className="text-gray-500 text-lg">
              Aucun lot disponible pour le moment. Revenez bientôt !
            </p>
          </div>
        ) : (
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
            {lots.map((lot) => (
              <Card
                key={lot.id}
                className="overflow-hidden card-hover relative"
                data-testid={`lot-${lot.id}`}
              >
                {lot.is_promotion && (
                  <span className="promo-badge">Promo</span>
                )}
                <div className="aspect-video overflow-hidden bg-gray-100">
                  <img
                    src={lot.image_url}
                    alt={lot.name}
                    className="w-full h-full object-cover"
                    onError={(e) => {
                      e.target.src = "https://images.unsplash.com/photo-1607623814075-e51df1bdc82f?w=600";
                    }}
                  />
                </div>
                <CardContent className="p-6">
                  <h3 className="font-heading text-xl font-semibold text-gray-900">
                    {lot.name}
                  </h3>
                  <p className="text-gray-600 mt-2">{lot.description}</p>
                  
                  {/* Lot Contents */}
                  <div className="mt-4 bg-muted/50 rounded-lg p-4">
                    <h4 className="text-sm font-semibold text-gray-700 mb-2">
                      Contenu du lot :
                    </h4>
                    <p className="text-sm text-gray-600 whitespace-pre-line">
                      {lot.contents}
                    </p>
                  </div>
                  
                  {/* Price */}
                  {lot.price && (
                    <div className="mt-4 flex items-center gap-2">
                      {lot.is_promotion && lot.promotion_price ? (
                        <>
                          <span className="text-2xl font-bold text-accent">
                            {lot.promotion_price}
                          </span>
                          <span className="text-lg text-gray-400 line-through">
                            {lot.price}
                          </span>
                        </>
                      ) : (
                        <span className="text-2xl font-bold text-primary">
                          {lot.price}
                        </span>
                      )}
                    </div>
                  )}
                </CardContent>
              </Card>
            ))}
          </div>
        )}
      </div>
    </div>
  );
}
